import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type Theme = 'light' | 'dark';
type Language = 'en' | 'ko';

interface UIState {
  theme: Theme;
  language: Language;
  sidebarOpen: boolean;
  selectedCountry: string;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  setLanguage: (language: Language) => void;
  toggleLanguage: () => void;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  setSelectedCountry: (country: string) => void;
  initializeTheme: () => void;
}

// Apply theme to DOM
const applyTheme = (theme: Theme) => {
  if (typeof window !== 'undefined') {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }
};

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      theme: 'light',
      language: 'en',
      sidebarOpen: true,
      selectedCountry: '',

      setTheme: (theme) => {
        applyTheme(theme);
        set({ theme });
      },

      toggleTheme: () => {
        const newTheme = get().theme === 'light' ? 'dark' : 'light';
        applyTheme(newTheme);
        set({ theme: newTheme });
      },

      setLanguage: (language) => set({ language }),

      toggleLanguage: () => {
        const newLanguage = get().language === 'en' ? 'ko' : 'en';
        set({ language: newLanguage });
      },

      setSidebarOpen: (open) => set({ sidebarOpen: open }),

      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),

      setSelectedCountry: (country) => set({ selectedCountry: country }),

      initializeTheme: () => {
        if (typeof window !== 'undefined') {
          const stored = localStorage.getItem('ui-storage');
          let theme: Theme = 'light';

          if (stored) {
            try {
              const parsed = JSON.parse(stored);
              theme = parsed.state?.theme || 'light';
            } catch {
              theme = 'light';
            }
          } else {
            const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            theme = prefersDark ? 'dark' : 'light';
          }

          applyTheme(theme);
          set({ theme });
        }
      },
    }),
    {
      name: 'ui-storage',
      partialize: (state) => ({ theme: state.theme, language: state.language, selectedCountry: state.selectedCountry }),
      onRehydrateStorage: () => (state) => {
        // Apply theme when store rehydrates from localStorage
        if (state) {
          applyTheme(state.theme);
        }
      },
    }
  )
);
